import React, { useState } from 'react';
import yaml from 'js-yaml';
import SyntaxHighlighter from 'react-syntax-highlighter/dist/cjs/light';
import yamlLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/yaml';
import './YamlView.css';

SyntaxHighlighter.registerLanguage('yaml', yamlLanguage);

/**
 * Type the same as the JSON view's (JsonPanel, react-json-view): its font and
 * size, and its rows' spacing, so flipping between the two keeps each line
 * where it was.
 */
const TYPE: React.CSSProperties = { fontFamily: 'monospace', fontSize: '14px', lineHeight: '28px' };

/**
 * GitLab's YAML colours — its light ("white") theme, which maps highlight.js's
 * types the same way: teal keys and list bullets, red strings, blue true /
 * false / null — each darkened just enough to keep WCAG AA (4.5:1) on every
 * background it is shown on, a diff's green and red included (DiffViewer).
 * Numbers, teal in GitLab beside its teal keys, are a green-teal here so the
 * two stay apart.
 */
export const YAML_PALETTE: { [token: string]: React.CSSProperties } = {
    hljs: { display: 'block', overflowX: 'auto', color: '#3a383f', background: 'transparent' },
    'hljs-attr': { color: '#006363' }, // GitLab #008080
    'hljs-bullet': { color: '#006363' },
    'hljs-string': { color: '#b00d36' }, // GitLab #dd1144
    'hljs-number': { color: '#11683f' }, // GitLab #009999
    'hljs-literal': { color: '#006080', fontWeight: 600 }, // GitLab #0086b3
    'hljs-comment': { color: '#6b6b5c', fontStyle: 'italic' }, // GitLab #999988
    'hljs-meta': { color: '#5f5f5f' }, // GitLab #999
    'hljs-type': { color: '#445588' }, // GitLab #445588
};

const colour = (token: string): React.CSSProperties => YAML_PALETTE[token] || {};

/** a mapping or a list with something in it: what folds */
const isBlock = (value: unknown): value is object =>
    typeof value === 'object' && value !== null && Object.keys(value).length > 0;

/** a key as YAML writes it, quoted where it has to be */
const keyText = (key: string) => yaml.dump({ [key]: 0 }, { lineWidth: -1 }).replace(/: 0\n$/, '');

/** a value that does not fold, as YAML writes it */
const dumpScalar = (value: unknown) => yaml.dump(value, { lineWidth: -1 }).replace(/\n$/, '');

/** the colour a scalar is written in */
const scalarColour = (value: unknown): React.CSSProperties => {
    if (typeof value === 'number') return colour('hljs-number');
    if (typeof value === 'boolean' || value === null) return colour('hljs-literal');
    if (typeof value === 'string') return colour('hljs-string');
    // an empty mapping or list
    return {};
};

/** "{2 keys}" / "[4 items]": a folded block, standing for what is in it */
const summary = (value: object) => {
    const size = Object.keys(value).length;
    return Array.isArray(value) ? `[${size} item${size === 1 ? '' : 's'}]` : `{${size} key${size === 1 ? '' : 's'}}`;
};

type Fold = { folded: Set<string>; toggle: (path: string) => void };

/**
 * One line, starting `col` characters in — real spaces, so YAML copied from
 * the page keeps its shape — and its caret, if it has one, just to the left
 * of where the line starts, outside the text.
 */
const Line = ({ col, caret, children }: { col: number; caret?: React.ReactNode; children: React.ReactNode }) => (
    <div className="nx-yaml__line">
        {caret && <span className="nx-yaml__caret-slot" style={{ left: `calc(${col}ch - 16px)` }}>{caret}</span>}
        {' '.repeat(col)}
        {children}
    </div>
);

/**
 * The lines of a scalar that starts on a line of its own making — after a
 * key's colon, or a list item's dash — whose content sits `col` characters
 * in. A string over several lines is a block: its marker on that first line,
 * the rest at `col`, each keeping whatever indentation js-yaml gave it past
 * the block's own (an indentation indicator, `|2-`, says how much).
 */
const scalarLines = (
    key: string,
    start: number,
    head: React.ReactNode,
    value: unknown,
    col: number,
): React.ReactNode[] => {
    const [first, ...rest] = dumpScalar(value).split('\n');
    const style = scalarColour(value);
    return [
        <Line key={key} col={start}>{head}<span style={style}>{first}</span></Line>,
        ...rest.map((row, i) => (
            // a blank line in the text is left blank, as js-yaml leaves it
            // eslint-disable-next-line react/no-array-index-key
            <Line key={`${key}#${i}`} col={row ? col : 0}><span style={style}>{row.replace(/^ {2}/, '')}</span></Line>
        )),
    ];
};

/**
 * The lines of a mapping or a list whose entries sit `col` characters in —
 * its keys there, or its dashes. `lead` is what is still to be written on its
 * first line, before it: the dashes of the list items it is the first thing
 * in, which YAML puts on one line ("- - - 1"), each one a level deeper than
 * the last. The first line starts that much earlier, so its own entry still
 * lands at `col`.
 */
const renderBlock = (value: object, col: number, path: string, fold: Fold, lead = ''): React.ReactNode[] => {
    const lines: React.ReactNode[] = [];
    const entries: [string, unknown][] = Array.isArray(value) ? value.map((item, i) => [String(i), item]) : Object.entries(value);
    entries.forEach(([key, item], index) => {
        const here = `${path}/${key}`;
        const pending = index === 0 ? lead : '';
        // where this entry's line starts: back by the dashes still to be written
        const start = col - pending.length;
        if (Array.isArray(value)) {
            const dashes = `${pending}- `;
            if (isBlock(item)) {
                // a list item that is itself a mapping or a list: its first
                // line on the dashes, its entries a step in
                lines.push(...renderBlock(item, col + 2, here, fold, dashes));
            } else {
                const head = <span style={colour('hljs-bullet')}>{dashes}</span>;
                lines.push(...scalarLines(here, start, head, item, col + 2));
            }
            return;
        }
        const head = (
            <>
                {pending && <span style={colour('hljs-bullet')}>{pending}</span>}
                <span style={colour('hljs-attr')}>{keyText(key)}</span>:
            </>
        );
        if (isBlock(item)) {
            const open = !fold.folded.has(here);
            const caret = (
                <button
                    type="button"
                    className="nx-yaml__caret"
                    aria-expanded={open}
                    aria-label={`${open ? 'Collapse' : 'Expand'} ${key}`}
                    onClick={() => fold.toggle(here)}
                >
                    {open ? '▾' : '▸'}
                </button>
            );
            lines.push(
                <Line key={here} col={start} caret={caret}>
                    {head}
                    {!open && <button type="button" className="nx-yaml__summary" onClick={() => fold.toggle(here)}> {summary(item)}</button>}
                </Line>,
            );
            // what a key holds is a step in, a list as much as a mapping
            if (open) lines.push(...renderBlock(item, col + 2, here, fold));
            return;
        }
        lines.push(...scalarLines(here, start, <>{head} </>, item, col + 2));
    });
    return lines;
};

/**
 * Data written out as YAML, in the palette above and the JSON view's type —
 * and, like the JSON view, with a caret by each key that holds a mapping or a
 * list, to fold it away to a summary and back. Built from the data rather
 * than from YAML text, so it knows where each block starts and ends; each
 * value is still written by js-yaml, so quoting is YAML's own.
 */
const YamlView = ({ data, className }: { data: unknown; className?: string }) => {
    const [folded, setFolded] = useState<Set<string>>(() => new Set());
    const toggle = (path: string) => setFolded((prev) => {
        const next = new Set(prev);
        if (next.has(path)) next.delete(path);
        else next.add(path);
        return next;
    });
    return (
        <div className={`nx-yaml${className ? ` ${className}` : ''}`} style={{ ...TYPE, ...colour('hljs') }}>
            {isBlock(data)
                ? renderBlock(data, 0, '', { folded, toggle })
                : scalarLines('', 0, null, data, 2)}
        </div>
    );
};

/** the same for a piece of a line in a diff: inline, on whatever its row is coloured */
const INLINE_PALETTE = {
    ...YAML_PALETTE,
    hljs: { display: 'inline', color: 'inherit', background: 'transparent' },
};

/**
 * One line of YAML — or a piece of one — highlighted in place, in the
 * palette above: for a diff, which lays out its own lines (DiffViewer).
 */
export const YamlLine = ({ text }: { text: string }) => (
    <SyntaxHighlighter
        language="yaml"
        style={INLINE_PALETTE}
        PreTag="span"
        CodeTag="span"
        customStyle={{ display: 'inline', padding: 0, margin: 0, background: 'transparent' }}
    >
        {text}
    </SyntaxHighlighter>
);

export default YamlView;
